import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, update, set } from 'firebase/database';
import { database } from '../config/firebase';
import AdminList from './AdminList';
import AdPreview from './AdPreview';
import AdminLogin from './AdminLogin';
import GoogleSheetsLoader from './GoogleSheetsLoader';
import EditAdForm from './EditAdForm';
import FacebookLeadsTab from './FacebookLeadsTab';
import './AdminPage.css';

// API base URL for image processing server
// Ensure API URL has protocol
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const API_BASE_URL = rawApiUrl.startsWith('http://') || rawApiUrl.startsWith('https://') 
  ? rawApiUrl 
  : `https://${rawApiUrl}`;


function AdminPage() {
  const [selectedAd, setSelectedAd] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('firebase'); // 'firebase', 'sheets', or 'facebook-leads'
  
  // Firebase state
  const [firebaseAds, setFirebaseAds] = useState([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const [firebaseError, setFirebaseError] = useState(null);
  
  // Google Sheets state
  const [sheetsAds, setSheetsAds] = useState([]);
  const [sheetsLoading, setSheetsLoading] = useState(true);
  const [sheetsError, setSheetsError] = useState(null);
  const [sheetsRefreshTrigger, setSheetsRefreshTrigger] = useState(0);
  
  // Edit form state
  const [editingAd, setEditingAd] = useState(null);
  const [editFormLoading, setEditFormLoading] = useState(false);
  
  // Video generation state - persisted in sessionStorage
  const [generatingVideoFor, setGeneratingVideoFor] = useState(() => {
    // Load from sessionStorage on mount
    const saved = sessionStorage.getItem('generatingVideoFor');
    return saved ? saved : null;
  });
  
  // Save generatingVideoFor to sessionStorage whenever it changes
  useEffect(() => {
    if (generatingVideoFor) {
      sessionStorage.setItem('generatingVideoFor', generatingVideoFor);
    } else {
      sessionStorage.removeItem('generatingVideoFor');
    }
  }, [generatingVideoFor]);
  
  // Clear generatingVideoFor state when the ad gets previewVideo (after generation completes)
  useEffect(() => {
    if (generatingVideoFor) {
      // Check Firebase ads
      const firebaseAd = firebaseAds.find(a => a.id === generatingVideoFor);
      if (firebaseAd && firebaseAd.previewVideo) {
        // Video was created, clear the generating state
        setGeneratingVideoFor(null);
        return;
      }
      
      // Check Google Sheets ads
      const sheetsAd = sheetsAds.find(a => a.id === generatingVideoFor);
      if (sheetsAd && sheetsAd.previewVideo) {
        // Video was created, clear the generating state
        setGeneratingVideoFor(null);
        return;
      }
    }
  }, [generatingVideoFor, firebaseAds, sheetsAds]);

  useEffect(() => {
    // Check if user is already authenticated (stored in sessionStorage)
    const authStatus = sessionStorage.getItem('adminAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch Firebase ads
  useEffect(() => {
    if (!isAuthenticated) return;

    const adsRef = ref(database, 'androidtv/ads/live');

    const unsubscribe = onValue(
      adsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const adsArray = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
            source: 'firebase'
          }));
          setFirebaseAds(adsArray);
        } else {
          setFirebaseAds([]);
        }
        setFirebaseLoading(false);
        setFirebaseError(null);
      },
      (error) => {
        console.error('Error fetching Firebase ads:', error);
        setFirebaseError('Failed to load ads from Firebase. Please check your connection.');
        setFirebaseLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('adminAuthenticated', 'true');
  };

  const handleSelectAd = (ad) => {
    setSelectedAd(ad);
  };

  const handleUpdateAd = async (adId, field, value) => {
    try {
      // Update Firebase first
      const adRef = ref(database, `androidtv/ads/live/${adId}`);
      await update(adRef, { [field]: value });
      console.log(`Successfully updated ${field} for ad ${adId} to ${value}`);
      
      // Update local state only after successful Firebase update
      setFirebaseAds(prevAds => 
        prevAds.map(ad => 
          ad.id === adId ? { ...ad, [field]: value } : ad
        )
      );
      
      // Also update selectedAd if it's the one being updated
      setSelectedAd(prevAd => 
        prevAd && prevAd.id === adId ? { ...prevAd, [field]: value } : prevAd
      );
    } catch (error) {
      console.error(`Error updating ${field} for ad ${adId}:`, error);
      alert(`Failed to update ${field}. Please try again.`);
    }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setSelectedAd(ad); // Also select the ad for consistency
  };

  const handleCreateNewAd = () => {
    // Create a new empty ad object with a flag indicating it's new
    const newAd = {
      id: '', // Will be set by user in the form
      isNew: true, // Flag to indicate this is a new ad
      business_name: '',
      business_email: '',
      business_phone: '',
      business_website: '',
      business_description: '',
      description: '',
      title: '',
      video: '',
      video_no_subs: '',
      vimeo: '',
      staticQRCODE: '',
      titleQRCODE: '',
      paid_until: '',
      debug: false,
      enabled: false,
      portrait: false,
      media: {}
    };
    setEditingAd(newAd);
    setSelectedAd(null); // Clear selected ad when creating new
  };

  const handleCancelEdit = () => {
    setEditingAd(null);
    setEditFormLoading(false);
  };

  const handleSaveAdEdit = async (updatedAdData) => {
    const isNewAd = editingAd && editingAd.isNew;
    const adId = isNewAd ? updatedAdData.id : editingAd.id;

    if (!adId || adId.trim() === '') {
      alert('יש להזין מזהה פרסומת');
      setEditFormLoading(false);
      return;
    }

    // Validate that ID doesn't contain invalid characters
    if (!/^[a-zA-Z0-9_-]+$/.test(adId)) {
      alert('מזהה הפרסומת יכול להכיל רק אותיות, מספרים, מקפים ותחתונים');
      setEditFormLoading(false);
      return;
    }

    // Check if ID already exists (only for new ads)
    if (isNewAd) {
      const existingAd = firebaseAds.find(ad => ad.id === adId);
      if (existingAd) {
        alert('מזהה הפרסומת כבר קיים. אנא בחר מזהה אחר.');
        setEditFormLoading(false);
        return;
      }
    }

    setEditFormLoading(true);
    
    try {
      const adRef = ref(database, `androidtv/ads/live/${adId}`);
      
      if (isNewAd) {
        // Generate secret for new ads (stored in Firebase)
        const adSecret = generateAdSecret();

        // Create new ad using set
        await set(adRef, {
          ...updatedAdData,
          secret: adSecret,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log(`Successfully created new ad ${adId}`);
        
        // Add to local state
        const newAdWithId = {
          id: adId,
          ...updatedAdData,
          secret: adSecret,
          source: 'firebase',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setFirebaseAds(prevAds => [...prevAds, newAdWithId]);
        setSelectedAd(newAdWithId);
        
        alert('הפרסומת נוצרה בהצלחה!');
      } else {
        // Update existing ad
        await update(adRef, {
          ...updatedAdData,
          updatedAt: new Date().toISOString()
        });
        console.log(`Successfully updated ad ${adId}`);
        
        // Update local state
        setFirebaseAds(prevAds => 
          prevAds.map(ad => 
            ad.id === adId ? { ...ad, ...updatedAdData, updatedAt: new Date().toISOString() } : ad
          )
        );
        
        // Update selectedAd if it's the one being edited
        setSelectedAd(prevAd => 
          prevAd && prevAd.id === adId ? { ...prevAd, ...updatedAdData, updatedAt: new Date().toISOString() } : prevAd
        );
        
      }
      
      // Clear editing state
      setEditingAd(null);
      setEditFormLoading(false);
    } catch (error) {
      console.error(`Error ${isNewAd ? 'creating' : 'updating'} ad ${adId}:`, error);
      alert(`שגיאה ב${isNewAd ? 'יצירת' : 'עדכון'} הפרסומת. נסה שוב.`);
      setEditFormLoading(false);
    }
  };

  const handleSheetsDataLoaded = (ads) => {
    setSheetsAds(ads);
    setSheetsLoading(false);
    setSheetsError(null);
  };

  const handleSheetsError = (error) => {
    setSheetsError(error);
    setSheetsLoading(false);
  };

  // Helper function to get next available ad index
  const getNextAdIndex = () => {
    const miluimAds = firebaseAds.filter(ad => ad.id && ad.id.startsWith('miluim-'));
    let maxIndex = -1;
    miluimAds.forEach(ad => {
      const match = ad.id.match(/^miluim-(\d+)$/);
      if (match) {
        const index = parseInt(match[1], 10);
        if (!isNaN(index)) {
          maxIndex = Math.max(maxIndex, index);
        }
      }
    });
    return maxIndex + 1;
  };

  // Generate a random secret for new ads (stored under androidtv/ads/live/{id}/secret)
  const generateAdSecret = () => {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  };

  // Helper function to format phone number for WhatsApp URL
  const formatPhone = (phone) => {
    if (!phone) return null;
    // Remove any non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Add country code if not present
    if (!digits.startsWith('972')) {
      return '972' + digits.replace(/^0+/, '');
    }
    return digits;
  };

  // Helper function to create Linkly URL (via backend to avoid CORS)
  const createLinklyUrl = async (targetUrl, name, adId, title, customName = null) => {
    if (!targetUrl) return null;
    
    // Use custom name if provided, otherwise use default format
    const linklyName = customName || (name && adId && title 
      ? `${name}-${adId} - ${title}` 
      : null);
    
    try {
      console.log(`📱 Calling backend API to create Linkly URL...`);
      console.log(`   Target URL: ${targetUrl}`);
      console.log(`   Link name: ${linklyName || 'Not provided'}`);
      
      const response = await fetch(`${API_BASE_URL}/api/create-linkly-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetUrl,
          linklyName
        })
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = data.full_url || targetUrl;
        console.log(`✅ Linkly URL created: ${fullUrl}`);
        return fullUrl;
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error creating Linkly URL:', errorData.error || 'Unknown error');
        return targetUrl;
      }
    } catch (error) {
      console.error('Error calling Linkly API:', error);
      return targetUrl;
    }
  };

  // Helper function to process QR code
  const processQrCode = async (ad, adId) => {
    // Always create WhatsApp Linkly link when creating ad from Google Sheets
    // Get phone number from column I first, then fallback to column D
    const whatsappPhone = ad.whatsapp_phone || ad.business_phone || ad.phone;
    
    if (!whatsappPhone || !whatsappPhone.trim()) {
      console.error('No phone number found for WhatsApp link');
      return null;
    }

    const formattedPhone = formatPhone(whatsappPhone);
    if (!formattedPhone) {
      console.error('Failed to format phone number');
      return null;
    }

    // Fixed WhatsApp message text: "הגעתי דרך GSRadio, אשמח לפרטים נוספים"
    const whatsappMessage = encodeURIComponent('הגעתי דרך GSRadio, אשמח לפרטים נוספים');
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${whatsappMessage}`;
    
    // Build link name: business_name-ad_id-business_description
    const businessName = ad.business_name || ad.name || '';
    const businessDescription = ad.business_description || '';
    const linklyName = `${businessName}-${adId}-${businessDescription}`;
    
    console.log(`📱 Creating WhatsApp Linkly link:`);
    console.log(`   WhatsApp URL: ${whatsappUrl}`);
    console.log(`   Phone: ${formattedPhone}`);
    console.log(`   Link name: ${linklyName}`);
    
    // Create Linkly link from WhatsApp URL
    const linklyUrl = await createLinklyUrl(whatsappUrl, businessName, adId, businessDescription, linklyName);
    
    if (linklyUrl) {
      console.log(`✅ Linkly link created: ${linklyUrl}`);
      console.log(`   This will be saved to staticQRCODE in Firebase`);
    } else {
      console.error(`❌ Failed to create Linkly link`);
    }
    
    return linklyUrl;
  };

  // Helper function to process image through API
  const processImage = async (imageUrl, index, total) => {
    try {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`📸 [FRONTEND] Processing image ${index + 1}/${total}`);
      console.log(`   URL: ${imageUrl}`);
      console.log(`   API_BASE_URL: ${API_BASE_URL}`);
      console.log(`   Full API URL: ${API_BASE_URL}/api/process-image`);
      console.log(`   Request body:`, JSON.stringify({ imageUrl }));
      
      const startTime = Date.now();
      const response = await fetch(`${API_BASE_URL}/api/process-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl }),
      });

      console.log(`📡 [FRONTEND] Response received:`);
      console.log(`   Status: ${response.status} ${response.statusText}`);
      console.log(`   Content-Type: ${response.headers.get('content-type')}`);

      if (!response.ok) {
        // Try to get error details
        let errorText = '';
        let errorData = null;
        try {
          errorText = await response.text();
          console.error(`❌ [FRONTEND] Response text:`, errorText.substring(0, 500));
          try {
            errorData = JSON.parse(errorText);
            console.error(`❌ [FRONTEND] Parsed error data:`, errorData);
          } catch (e) {
            console.error(`❌ [FRONTEND] Response is not JSON, it's: ${errorText.substring(0, 100)}`);
          }
        } catch (e) {
          console.error(`❌ [FRONTEND] Failed to read error response:`, e);
        }
        
        console.error(`❌ [FRONTEND] API request failed: ${response.status} ${response.statusText}`);
        console.error(`   Error data:`, errorData);
        console.error(`   Error text:`, errorText.substring(0, 200));
        throw new Error(errorData?.error || errorText || 'Failed to process image');
      }

      const responseText = await response.text();
      console.log(`📄 [FRONTEND] Response text (first 200 chars):`, responseText.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error(`❌ [FRONTEND] Failed to parse JSON response:`, e);
        console.error(`   Response text:`, responseText);
        throw new Error('Invalid JSON response from server');
      }
      
      const duration = Date.now() - startTime;
      console.log(`✅ [FRONTEND] Image ${index + 1}/${total} processed successfully`);
      console.log(`   Processed URL: ${data.url}`);
      console.log(`   Cached: ${data.cached ? 'Yes (already existed)' : 'No (newly uploaded)'}`);
      console.log(`   Processing time: ${duration}ms`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return data.url;
    } catch (error) {
      console.error(`❌ [FRONTEND] Error processing image ${index + 1}/${total}:`, error);
      console.error(`   Error name: ${error.name}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error stack:`, error.stack);
      console.error(`   Using original URL as fallback: ${imageUrl}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // Return original URL if processing fails
      return imageUrl;
    }
  };

  // Helper function to process video through API
  const processVideo = async (videoUrl) => {
    try {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`🎥 [FRONTEND] Processing video`);
      console.log(`   URL: ${videoUrl}`);
      console.log(`   API_BASE_URL: ${API_BASE_URL}`);
      console.log(`   Full API URL: ${API_BASE_URL}/api/process-video`);
      console.log(`   Request body:`, JSON.stringify({ videoUrl }));
      
      const startTime = Date.now();
      const response = await fetch(`${API_BASE_URL}/api/process-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoUrl }),
      });

      console.log(`📡 [FRONTEND] Response received:`);
      console.log(`   Status: ${response.status} ${response.statusText}`);
      console.log(`   Headers:`, Object.fromEntries(response.headers.entries()));
      console.log(`   Content-Type: ${response.headers.get('content-type')}`);

      if (!response.ok) {
        // Try to get error details
        let errorText = '';
        let errorData = null;
        try {
          errorText = await response.text();
          console.error(`❌ [FRONTEND] Response text:`, errorText.substring(0, 500));
          try {
            errorData = JSON.parse(errorText);
            console.error(`❌ [FRONTEND] Parsed error data:`, errorData);
          } catch (e) {
            console.error(`❌ [FRONTEND] Response is not JSON, it's: ${errorText.substring(0, 100)}`);
          }
        } catch (e) {
          console.error(`❌ [FRONTEND] Failed to read error response:`, e);
        }
        
        console.error(`❌ [FRONTEND] API request failed: ${response.status} ${response.statusText}`);
        console.error(`   Error data:`, errorData);
        console.error(`   Error text:`, errorText.substring(0, 200));
        throw new Error(errorData?.error || errorText || 'Failed to process video');
      }

      const responseText = await response.text();
      console.log(`📄 [FRONTEND] Response text (first 200 chars):`, responseText.substring(0, 200));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error(`❌ [FRONTEND] Failed to parse JSON response:`, e);
        console.error(`   Response text:`, responseText);
        throw new Error('Invalid JSON response from server');
      }
      
      const duration = Date.now() - startTime;
      console.log(`✅ [FRONTEND] Video processed successfully`);
      console.log(`   Processed URL: ${data.url}`);
      console.log(`   Cached: ${data.cached ? 'Yes (already existed)' : 'No (newly uploaded)'}`);
      console.log(`   Processing time: ${duration}ms`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return data.url;
    } catch (error) {
      console.error(`❌ [FRONTEND] Error processing video:`, error);
      console.error(`   Error name: ${error.name}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error stack:`, error.stack);
      console.error(`   Using original URL as fallback: ${videoUrl}`);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      // Return original URL if processing fails
      return videoUrl;
    }
  };

  // Handle generating preview video
  const handleGeneratePreviewVideo = async (ad, source, onComplete) => {
    const startTime = Date.now();
    
    // Determine ad name for video file
    let adName;
    if (source === 'firebase') {
      adName = ad.id || 'unknown';
    } else {
      // For Google Sheets, we need to extract the ad name or use a default
      adName = ad.id || `sheets-${ad.sheetsAdLineNumber || 'unknown'}`;
    }
    
    // Use ad.id for state tracking (consistent with AdminList)
    const adIdForState = ad.id || adName;
    
    // Set generating state immediately - use ad.id for consistency
    setGeneratingVideoFor(adIdForState);
    
    try {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🎬 [PREVIEW VIDEO] Starting preview video generation');
      console.log('═══════════════════════════════════════════════════════════');
      
      console.log(`📋 [STEP 1] Ad information:`);
      console.log(`   Ad name: ${adName}`);
      console.log(`   Source: ${source}`);
      
      // For Google Sheets ads, create WhatsApp QR code if staticQRCODE is empty
      let qrCodeUrl = ad.staticQRCODE || '';
      if (source === 'sheets' && !qrCodeUrl) {
        console.log(`\n📱 [STEP 1.1] Creating WhatsApp QR code for Google Sheets ad...`);
        const whatsappPhone = ad.whatsapp_phone || ad.business_phone || ad.phone;
        
        if (whatsappPhone && whatsappPhone.trim()) {
          const formattedPhone = formatPhone(whatsappPhone);
          if (formattedPhone) {
            // WhatsApp message: "ברצוני לפרסם באמצעות GSRadioTV" (URL-encoded)
            const whatsappMessage = '%D7%91%D7%A8%D7%A6%D7%95%D7%A0%D7%99%20%D7%9C%D7%A4%D7%A8%D7%A1%D7%9D%20%D7%91%D7%90%D7%9E%D7%A6%D7%A2%D7%95%D7%AA%20GSRadioTV';
            qrCodeUrl = `https://wa.me/${formattedPhone}?text=${whatsappMessage}`;
            console.log(`✅ [STEP 1.1] WhatsApp QR code URL created:`);
            console.log(`   Phone: ${formattedPhone}`);
            console.log(`   URL: ${qrCodeUrl}`);
          } else {
            console.warn(`⚠️  [STEP 1.1] Failed to format phone number: ${whatsappPhone}`);
          }
        } else {
          console.warn(`⚠️  [STEP 1.1] No phone number found for WhatsApp QR code`);
        }
      }
      
      // Collect ad data
      const adData = {
        title: ad.title || '',
        description: ad.description || '',
        media: ad.media || {},
        video: ad.video || '',
        vimeo: ad.vimeo || ad.youtubeID || '',
        staticQRCODE: qrCodeUrl,
        titleQRCODE: ad.titleQRCODE || '',
        portrait: ad.portrait || false
      };
      
      console.log(`📦 [STEP 2] Collected ad data:`);
      console.log(`   Title: ${adData.title}`);
      console.log(`   Has image: ${!!(adData.media?.[0]?.image || adData.media?.['0']?.image)}`);
      console.log(`   Has video: ${!!(adData.video || adData.vimeo)}`);
      console.log(`   Has QR code: ${!!adData.staticQRCODE}`);
      
      // Call backend API to generate video
      console.log(`\n📡 [STEP 3] Calling backend API to generate video...`);
      console.log(`   API URL: ${API_BASE_URL}/api/generate-preview-video`);
      
      const response = await fetch(`${API_BASE_URL}/api/generate-preview-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adName,
          adData
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to generate preview video');
      }
      
      const result = await response.json();
      const videoUrl = result.url;
      
      console.log(`✅ [STEP 3] Video generated successfully`);
      console.log(`   Video URL: ${videoUrl}`);
      
      // Save video URL based on source
      if (source === 'firebase') {
        console.log(`\n💾 [STEP 4] Saving video URL to Firebase...`);
        const adRef = ref(database, `androidtv/ads/live/${adName}`);
        await update(adRef, { previewVideo: videoUrl });
        
        // Update local state
        setFirebaseAds(prevAds =>
          prevAds.map(a =>
            a.id === adName ? { ...a, previewVideo: videoUrl } : a
          )
        );
        
        // Update selected ad if it's the one being updated
        if (selectedAd && selectedAd.id === adName) {
          setSelectedAd(prevAd => ({ ...prevAd, previewVideo: videoUrl }));
        }
        
        console.log(`✅ [STEP 4] Video URL saved to Firebase`);
      } else {
        // Google Sheets - update via API
        console.log(`\n💾 [STEP 4] Saving video URL to Google Sheets...`);
        const rowNumber = ad.sheetsAdLineNumber || ad._rawData?.rowIndex;
        
        if (rowNumber) {
          const updateResponse = await fetch(`${API_BASE_URL}/api/update-google-sheet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              rowNumber,
              previewVideoUrl: videoUrl
            }),
          });
          
          if (updateResponse.ok) {
            console.log(`✅ [STEP 4] Video URL saved to Google Sheets`);
            
            // Update local state immediately (before refresh)
            setSheetsAds(prevAds =>
              prevAds.map(a => {
                const adRowNumber = a.sheetsAdLineNumber || a._rawData?.rowIndex;
                if (adRowNumber === rowNumber) {
                  console.log(`   Updating local state for ad with rowNumber: ${rowNumber}`);
                  return { ...a, previewVideo: videoUrl };
                }
                return a;
              })
            );
            
            // Update selected ad if it's the one being updated
            if (selectedAd) {
              const selectedRowNumber = selectedAd.sheetsAdLineNumber || selectedAd._rawData?.rowIndex;
              if (selectedRowNumber === rowNumber) {
                console.log(`   Updating selected ad with previewVideo`);
                setSelectedAd(prevAd => ({ ...prevAd, previewVideo: videoUrl }));
              }
            }
            
            // Refresh Google Sheets data to get latest from server
            console.log(`   Triggering Google Sheets data refresh...`);
            setSheetsRefreshTrigger(prev => prev + 1);
          } else {
            const errorText = await updateResponse.text();
            console.error(`❌ [STEP 4] Failed to save video URL to Google Sheets`);
            console.error(`   Response status: ${updateResponse.status}`);
            console.error(`   Response text: ${errorText.substring(0, 200)}`);
          }
        } else {
          console.error(`❌ [STEP 4] No row number found, cannot update Google Sheets`);
        }
      }
      
      const duration = Date.now() - startTime;
      console.log(`\n🎉 [SUCCESS] Preview video generation completed!`);
      console.log(`   Video URL: ${videoUrl}`);
      console.log(`⏱️  Total time: ${duration}ms`);
      console.log('═══════════════════════════════════════════════════════════');
      
      // Clear generating state
      setGeneratingVideoFor(null);
      
      // Call completion callback if provided
      if (onComplete) {
        onComplete();
      }
      
      alert(`הוידאו המקדים נוצר בהצלחה!\nמזהה פרסומת: ${adName}`);
      
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`\n❌ [ERROR] Preview video generation failed!`);
      console.error(`   Error: ${error.message}`);
      console.error(`   Stack: ${error.stack}`);
      console.log(`⏱️  Time before error: ${duration}ms`);
      console.log('═══════════════════════════════════════════════════════════');
      
      // Clear generating state on error
      setGeneratingVideoFor(null);
      
      // Call completion callback on error too to clear loading state
      if (onComplete) {
        onComplete();
      }
      
      alert(`שגיאה ביצירת הוידאו המקדים: ${error.message}\nמזהה פרסומת: ${adName}`);
    }
  };

  // Handle removing preview video
  const handleRemovePreviewVideo = async (ad, source) => {
    if (!window.confirm('האם אתה בטוח שברצונך להסיר את הוידאו המקדים?')) {
      return;
    }

    try {
      const adName = ad.id || (source === 'firebase' ? ad.id : `sheets-${ad.sheetsAdLineNumber || 'unknown'}`);
      
      if (source === 'firebase') {
        // Remove from Firebase
        const adRef = ref(database, `androidtv/ads/live/${adName}`);
        await update(adRef, { previewVideo: null });
        
        // Update local state
        setFirebaseAds(prevAds =>
          prevAds.map(a =>
            a.id === adName ? { ...a, previewVideo: null } : a
          )
        );
        
        // Update selected ad if it's the one being updated
        if (selectedAd && selectedAd.id === adName) {
          setSelectedAd(prevAd => ({ ...prevAd, previewVideo: null }));
        }
      } else {
        // Google Sheets - update via API
        const rowNumber = ad.sheetsAdLineNumber || ad._rawData?.rowIndex;
        const adName = ad.id || `sheets-${rowNumber || 'unknown'}`;
        
        if (rowNumber) {
          const updateResponse = await fetch(`${API_BASE_URL}/api/update-google-sheet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              rowNumber,
              adName: adName, // Required by the endpoint
              linklyUrl: ad.linklyUrl || '', // Pass existing Linkly URL to preserve it
              previewVideoUrl: null // Set to null to remove
            }),
          });
          
          if (updateResponse.ok) {
            // Update local state immediately
            setSheetsAds(prevAds =>
              prevAds.map(a =>
                a.id === ad.id ? { ...a, previewVideo: null } : a
              )
            );
            if (selectedAd && selectedAd.id === ad.id) {
              setSelectedAd(prevAd => ({ ...prevAd, previewVideo: null }));
            }
            // Trigger a full refresh as well
            setSheetsRefreshTrigger(prev => prev + 1);
          } else {
            const errorData = await updateResponse.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || 'Failed to remove video URL from Google Sheets');
          }
        } else {
          throw new Error('No row number found, cannot update Google Sheets');
        }
      }
      
      alert(`הוידאו המקדים הוסר בהצלחה!\nמזהה פרסומת: ${adName}`);
    } catch (error) {
      console.error('Error removing preview video:', error);
      alert(`שגיאה בהסרת הוידאו המקדים: ${error.message}`);
    }
  };

  // Handle creating ad from Google Sheets data
  const handleCreateAd = async (sheetsAd) => {
    const startTime = Date.now();
    try {
      console.log('═══════════════════════════════════════════════════════════');
      console.log('🚀 [AD CREATION] Starting ad creation process');
      console.log('═══════════════════════════════════════════════════════════');
      
      // Get next available ad index
      console.log(`🔢 [STEP 1] Getting next available ad index...`);
      const nextIndex = getNextAdIndex();
      const adName = `miluim-${nextIndex}`;
      const adId = String(nextIndex);
      console.log(`✅ [STEP 1] Next ad index: ${nextIndex}`);
      console.log(`   Ad name: ${adName}`);
      console.log(`   Ad ID: ${adId}`);

      // Process description - add prefix if not present
      console.log(`📝 [STEP 2] Processing description...`);
      let description = sheetsAd.description || '';
      let title = "מחבקים מילואימניקים 🎗️";


      // Process media - convert from object with numeric keys to array and process images
      console.log(`🖼️  [STEP 3] Processing media/images...`);
      const media = [];
      if (sheetsAd.media) {
        // Handle both array and object formats
        const mediaItems = Array.isArray(sheetsAd.media) 
          ? sheetsAd.media 
          : Object.keys(sheetsAd.media).map(key => sheetsAd.media[key]);
        
        console.log(`   Found ${mediaItems.length} media item(s)`);
        
        // Process each image through the API
        for (let i = 0; i < mediaItems.length; i++) {
          const mediaItem = mediaItems[i];
          if (mediaItem && mediaItem.image) {
            console.log(`\n   Processing media item ${i + 1}/${mediaItems.length}:`);
            console.log(`   - Name: ${mediaItem.name || 'N/A'}`);
            console.log(`   - Original URL: ${mediaItem.image}`);
            
            // Process image through API (download, resize, upload to FTP)
            const processedUrl = await processImage(mediaItem.image, i, mediaItems.length);
            media.push({
              image: processedUrl,
              name: mediaItem.name || ''
            });
            console.log(`   ✅ Media item ${i + 1} processed`);
          }
        }
        console.log(`✅ [STEP 3] All media items processed (${media.length} total)`);
      } else {
        console.log(`ℹ️  [STEP 3] No media items found`);
      }

      // Validate phone number for WhatsApp (column I or D)
      console.log(`\n📞 [STEP 4] Validating phone number for WhatsApp...`);
      const whatsappPhone = sheetsAd.whatsapp_phone || sheetsAd.business_phone || sheetsAd.phone;
      if (!whatsappPhone || !whatsappPhone.trim()) {
        const errorMessage = 'שגיאה: לא נמצא מספר טלפון לפרסומת. לא ניתן ליצור פרסומת ללא מספר טלפון.';
        console.error(`❌ [ERROR] ${errorMessage}`);
        console.log('═══════════════════════════════════════════════════════════');
        alert(errorMessage);
        return; // Stop ad creation
      }
      console.log(`✅ [STEP 4] Phone number found: ${whatsappPhone}`);

      // Process video
      console.log(`\n🎥 [STEP 5] Processing video...`);
      let processedVideoUrl = null;
      const originalVideoUrl = sheetsAd.vimeo || sheetsAd.youtubeID;
      if (originalVideoUrl) {
        console.log(`   Original video URL: ${originalVideoUrl}`);
        // Process video through API (download and upload to FTP)
        processedVideoUrl = await processVideo(originalVideoUrl);
        console.log(`✅ [STEP 4] Video processed: ${processedVideoUrl}`);
        
        // Check if video was successfully uploaded to FTP
        if (!processedVideoUrl || !processedVideoUrl.includes('greenshpits.com')) {
          // Video processing failed - don't create the ad
          const errorMessage = processedVideoUrl 
            ? `שגיאה: הוידאו לא הועלה ל-FTP. ה-URL שהתקבל: ${processedVideoUrl}`
            : `שגיאה: לא ניתן לעבד את הוידאו. אנא נסה שוב.`;
          
          console.error(`❌ [ERROR] Video was not uploaded to FTP`);
          console.error(`   Original URL: ${originalVideoUrl}`);
          console.error(`   Processed URL: ${processedVideoUrl || 'null'}`);
          console.log('═══════════════════════════════════════════════════════════');
          alert(errorMessage);
          return; // Stop ad creation
        }
        
        console.log(`✅ [STEP 5] Video successfully uploaded to FTP: ${processedVideoUrl}`);
      } else {
        console.log(`ℹ️  [STEP 5] No video URL found`);
        alert('שגיאה: לא נמצא וידאו לפרסומת. לא ניתן ליצור פרסומת ללא וידאו.');
        return; // Stop ad creation
      }

      // Process QR code
      console.log(`\n📱 [STEP 6] Processing QR code...`);
      const qrCode = await processQrCode(sheetsAd, adId);
      if (qrCode) {
        console.log(`✅ [STEP 6] QR code processed: ${qrCode}`);
      } else {
        console.log(`ℹ️  [STEP 6] No QR code created`);
      }

      // Build ad data structure
      console.log(`\n📦 [STEP 7] Building ad data structure...`);
      const adData = {
        business_description: sheetsAd.business_description || '',
        business_email: sheetsAd.business_email || '',
        business_name: sheetsAd.business_name || sheetsAd.name || '',
        business_phone: sheetsAd.business_phone || sheetsAd.phone || '',
        business_website: sheetsAd.business_website || '',
        debug: true, // Test mode
        enabled: false,
        description: description,
        title: title,
        media: media,
        paid_until: sheetsAd.paid_until || '',
        titleQRCODE: sheetsAd.titleQRCODE || '',
        video: processedVideoUrl // Video URL from FTP
      };

      console.log(`   ✅ Added processed video URL from FTP to ad data: ${processedVideoUrl}`);

      // Add QR code (Linkly link) to staticQRCODE
      if (qrCode) {
        adData.staticQRCODE = qrCode;
        console.log(`   ✅ Added Linkly link to staticQRCODE: ${qrCode}`);
      } else {
        console.log(`   ⚠️  No Linkly link created, staticQRCODE will not be set`);
      }
      
      console.log(`✅ [STEP 7] Ad data structure built:`);
      console.log(`   - Business name: ${adData.business_name}`);
      console.log(`   - Media items: ${adData.media.length}`);
      console.log(`   - Video URL: ${adData.video || 'None'}`);
      console.log(`   - QR Code: ${adData.staticQRCODE || 'None'}`);
      console.log(`   - Debug mode: ${adData.debug}`);
      console.log(`   - Enabled: ${adData.enabled}`);

      // Save to Firebase
      console.log(`\n💾 [STEP 8] Saving ad to Firebase...`);
      console.log(`   Path: androidtv/ads/live/${adName}`);
      const adRef = ref(database, `androidtv/ads/live/${adName}`);
      await set(adRef, adData);
      
      const duration = Date.now() - startTime;
      console.log(`✅ [STEP 8] Ad saved to Firebase successfully`);
      
      // Update Google Sheets - column U with ad name and column V with Linkly URL
      console.log(`\n📝 [STEP 9] Updating Google Sheets...`);
      // Use sheetsAdLineNumber which is the original row number from Google Sheets
      const rowNumber = sheetsAd.sheetsAdLineNumber || sheetsAd._rawData?.rowIndex;
      
      if (rowNumber) {
        try {
          console.log(`   📊 Row identification:`);
          console.log(`      - Ad ID: ${sheetsAd.id || 'N/A'}`);
          console.log(`      - sheetsAdLineNumber (original row from Google Sheets): ${sheetsAd.sheetsAdLineNumber || 'N/A'}`);
          console.log(`      - Fallback rowIndex from _rawData: ${sheetsAd._rawData?.rowIndex || 'N/A'}`);
          console.log(`      - Using row number: ${rowNumber}`);
          console.log(`   📝 Update details:`);
          console.log(`      - Row number in Google Sheets: ${rowNumber}`);
          console.log(`      - Ad name: ${adName}`);
          console.log(`      - Linkly URL: ${qrCode || 'Not available'}`);
          console.log(`      - API: ${API_BASE_URL}/api/update-google-sheet`);
          
          const updateResponse = await fetch(`${API_BASE_URL}/api/update-google-sheet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              rowNumber, 
              adName,
              linklyUrl: qrCode || null
            }),
          });

          if (updateResponse.ok) {
            const updateData = await updateResponse.json();
            console.log(`✅ [STEP 9] Google Sheets updated successfully`);
            console.log(`   Updated cells: ${updateData.updatedCells}`);
            
            // Refresh Google Sheets data to show updated adCreated status
            console.log(`\n🔄 [STEP 10] Refreshing Google Sheets data...`);
            setSheetsRefreshTrigger(prev => prev + 1);
            console.log(`✅ [STEP 10] Google Sheets data refresh triggered`);
          } else {
            const errorData = await updateResponse.json();
            console.error(`❌ [STEP 9] Failed to update Google Sheets: ${errorData.error || 'Unknown error'}`);
            console.error(`   Details: ${errorData.details || ''}`);
            // Don't fail the entire process if Google Sheets update fails
          }
        } catch (error) {
          console.error(`❌ [STEP 9] Error updating Google Sheets:`, error);
          // Don't fail the entire process if Google Sheets update fails
        }
      } else {
        console.log(`ℹ️  [STEP 9] No row number found in ad data, skipping Google Sheets update`);
      }
      
      console.log(`\n🎉 [SUCCESS] Ad creation completed successfully!`);
      console.log(`   Ad name: ${adName}`);
      console.log(`⏱️  Total time: ${duration}ms`);
      console.log('═══════════════════════════════════════════════════════════');
      
      alert(`הפרסומת ${adName} נוצרה בהצלחה בפיירבייס במצב test!`);

      // Refresh Firebase ads to show the new ad
      // The useEffect will automatically update the list
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`\n❌ [ERROR] Ad creation failed!`);
      console.error(`   Error: ${error.message}`);
      console.error(`   Stack: ${error.stack}`);
      console.log(`⏱️  Time before error: ${duration}ms`);
      console.log('═══════════════════════════════════════════════════════════');
      alert(`שגיאה ביצירת הפרסומת: ${error.message}`);
    }
  };

  // Reset sheets loading when switching to sheets tab
  useEffect(() => {
    if (activeTab === 'sheets' && sheetsAds.length === 0 && !sheetsError) {
      setSheetsLoading(true);
    }
  }, [activeTab, sheetsAds.length, sheetsError]);

  // Reset selected ad when switching tabs
  useEffect(() => {
    setSelectedAd(null);
  }, [activeTab]);

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Determine which ads to show based on active tab
  const currentAds = activeTab === 'firebase' ? firebaseAds : sheetsAds;
  const currentLoading = activeTab === 'firebase' ? firebaseLoading : sheetsLoading;
  const currentError = activeTab === 'firebase' ? firebaseError : sheetsError;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-header-content">
          <h1>Ads Management</h1>
          <div className="admin-page-header-actions">
            {activeTab === 'firebase' && (
              <button
                className="create-new-ad-button"
                onClick={handleCreateNewAd}
                disabled={!!editingAd}
              >
                ➕ יצור פרסומת חדשה
              </button>
            )}
            <Link to="/" className="back-to-home">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="admin-page-tabs">
        <button
          className={`admin-tab ${activeTab === 'firebase' ? 'active' : ''}`}
          onClick={() => setActiveTab('firebase')}
        >
          🔥 Firebase Ads
        </button>
        <button
          className={`admin-tab ${activeTab === 'sheets' ? 'active' : ''}`}
          onClick={() => setActiveTab('sheets')}
        >
          📊 Google Sheets Ads
        </button>
        <button
          className={`admin-tab ${activeTab === 'facebook-leads' ? 'active' : ''}`}
          onClick={() => setActiveTab('facebook-leads')}
        >
          📱 Facebook Leads
        </button>
      </div>

      {/* Google Sheets Loader - loads data when sheets tab is selected */}
      {activeTab === 'sheets' && (
        <GoogleSheetsLoader
          onDataLoaded={handleSheetsDataLoaded}
          onError={handleSheetsError}
          dataLoaded={sheetsAds.length > 0}
          refreshTrigger={sheetsRefreshTrigger}
        />
      )}

      {/* Facebook Leads Tab - full width */}
      {activeTab === 'facebook-leads' ? (
        <FacebookLeadsTab />
      ) : (
        <div className="admin-page-content">
          <div className="admin-page-sidebar">
            <AdminList
              ads={currentAds}
              loading={currentLoading}
              error={currentError}
              onSelectAd={handleSelectAd}
              selectedAdId={selectedAd?.id}
              onUpdateAd={handleUpdateAd}
              onCreateAd={handleCreateAd}
              onGeneratePreviewVideo={handleGeneratePreviewVideo}
              onRemovePreviewVideo={handleRemovePreviewVideo}
              generatingVideoFor={generatingVideoFor}
              source={activeTab}
              onEditAd={activeTab === 'firebase' ? handleEditAd : undefined}
            />
          </div>
          <div className="admin-page-main">
            {editingAd ? (
              <EditAdForm
                ad={editingAd}
                onSave={handleSaveAdEdit}
                onCancel={handleCancelEdit}
                loading={editFormLoading}
              />
            ) : (
              <AdPreview ad={selectedAd} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
